import { Button, Card, IconButton, Accordion } from 'components/common';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { CRM_LEGAL_STATUS, CRM_NUMBER_OF_EMPLOYEES, CRM_TYPE_OF_CUSTOMER } from 'app_config';
import { Controller, useForm } from 'react-hook-form';
import React, { useEffect, useMemo, useState } from 'react';
import { createCustomer, updateCustomer } from 'redux/crm/customerSlice';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { getCountries } from 'redux/settingsSlice';
import PhoneInput from 'react-phone-input-2';
import {isValidPhoneNumber} from 'libphonenumber-js';
import 'react-phone-input-2/lib/style.css';
import CustomerUploadLogo from './CustomerUploadLogo';
import GoogleMapAutoCompletePlaceDebounce from 'components/common/google-map-places/GoogleMapAutoCompletePlaceDebounce';
import { getIndustries } from 'redux/crm/settingSlice';
import { makeEmployeeLoaderWithDetails } from 'helpers/debounce';
import EmployeeSelectField from 'components/common/select/EmployeeSelectField';
import { FaArrowLeft, FaBan, FaSave } from 'react-icons/fa';
import PropTypes from 'prop-types';

import { INPUT_CLS as INPUT_CLASSES } from 'helpers/formStyles';
const CustomerForm = ({
  title,
  customer,
  embedded = false,
  onSuccess,
  onCancel
}) => {
  const {industryData: industries} = useSelector((state) => state.crm_setting);
  const {data: profileUser} = useSelector((state) => state.profile_user)
  const {countryData} = useSelector((state) => state.settings);
  const [activeKey, setActiveKey] = useState(['0', '1', '2']);

  const {control, register, handleSubmit, formState: { errors}, setValue, getValues } = useForm();
  const customertUrl = useMemo(() => '/crm/customers', []);
  // Load all active employees (value === label === name) for the responsible selects.
  const employeeOptions = useMemo(() => makeEmployeeLoaderWithDetails(), []);

  const numberOfEmployeesOptions = useMemo(() => CRM_NUMBER_OF_EMPLOYEES.map((option) => ({ value: option, label: option })), [])
  const defaultNumberOfEmployeesOption = useMemo(() => (
    customer ? { value: customer.employess, label: customer.employess } : numberOfEmployeesOptions[0]
  ), [customer, numberOfEmployeesOptions]);

  const typeOfCustomerOptions = useMemo(() => CRM_TYPE_OF_CUSTOMER.map((option) => ({ value: option, label: option })), [])
  const defaultTypeOfCustomerOption = useMemo(() => (
    customer ? { value: customer.type_of_company, label: customer.type_of_company } : typeOfCustomerOptions[0]
  ), [customer, typeOfCustomerOptions]);

  const defaultMainContactOption = useMemo(() => {
    if (customer && customer.main_contact) {
      return { value: customer.main_contact, label: customer.main_contact };
    }
    return null;
  }, [customer]);

  const defaultSecondaryContactOption = useMemo(() => {
    if (customer && customer.secondary_contact) {
      return customer.secondary_contact.map((option) => ({value: option, label: option}));
    }
    return null;
  }, [customer]);

  const industryOptions = useMemo(() => industries && industries.map((item) => ({ value: item.name, label: item.name })), [industries]);
  const defaultIndustryOption = useMemo(() => (
    customer ? { value: customer.industry, label: customer.industry } : industryOptions[0]
  ), [customer, industryOptions]);

  const countryOptions = useMemo(() => countryData && countryData.map((option) => ({ value: option.country, label: option.name })), [countryData]);
  const defaultcountryOption = useMemo(() => (
    customer ? { value: customer.country, label: customer.country_name } : (countryOptions.filter((country) => country.value === 'VN')[0])
  ), [customer, countryOptions]);

  const [logo, setLogo] = useState('');
  const [countryCode, setCountryCode] = useState('vn');
  const [address, setAddress] = useState({});

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const getIndustryList = () => {
      dispatch(getIndustries())
    }

    const getCountryList = () => {
      dispatch(getCountries())
    }

    getCountryList()
    getIndustryList()
  }, [dispatch]);

  useEffect(() => {
    setValue("country", profileUser?.site.country);
    setValue("main_contact", profileUser?.name);
    setValue('employess', numberOfEmployeesOptions[1]?.value);
    setValue('industry', industryOptions[1]?.value);
    setValue('type_of_company', typeOfCustomerOptions[1]?.value);

    if(customer) {
      Object.entries(customer).forEach(([key, value]) => {
        setValue(key, value)
      })
    }
    // mount-only: seed form defaults once; re-running on data refresh would overwrite user input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue]);


  const onSubmit = async (data) => {
    if (!validate(data)) return;

    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      let newValue = value;

      if (newValue === undefined) {
        delete data[key];
      }

      if (newValue === "null") {
        formData.append(key, "");
      }

      if (Array.isArray(newValue)) {
        if (newValue.length > 0) {
          newValue.forEach((item) => {
            formData.append(`${key}[]`, item);
          })
        }
        else
        {
          formData.append(`${key}[]`, []);
        }
      }
      else {
        formData.append(key, newValue);
      }
    })

    if (logo) {
      formData.append('logo', logo[0]);
    }

    if (address) {
      formData.append('address', JSON.stringify(address));
    }

    try {
      let customerId = null;
      if (customer)
      {
        customerId = customer.id;
        const response = await dispatch(updateCustomer({customerId, formData}));
        if (response.error) return; // rejected → slice already surfaces the error
      }
      else
      {
        const response = await dispatch(createCustomer(formData));
        if (response.error) return;
        customerId = response.payload.data.id;
      }

      if (customerId) {
        // When embedded (e.g. in a Drawer) defer to the caller; otherwise navigate.
        if (onSuccess) {
          onSuccess(customerId);
          return;
        }
        redirectCustomerDetail(customerId);
      }
    }
    catch(error) {
      toast.error(error.message);
    }
  }

  const handleCancle = () => {
    if (onCancel) return onCancel();
    navigate(-1);
    return null;
  }

  const redirectCustomerDetail = (customerId) => {
    navigate(`${customertUrl}/${customerId}`);
  }

  const validatePhoneNumber = (number) => {

    if (number === undefined ||
        number === 'undefined' ||
        number === null ||
        number === "null" ||
        number?.trim() === '') return undefined

    const {country} = getValues();
    const countryCode = country.toUpperCase();

    if (!isValidPhoneNumber(number.toString(), countryCode)) {
      return `Invalid phone number with country code '${countryCode}' `;
    }
    return true;
  }

  const validate = (data) => {
    if ((data.email === '' || data.email === undefined) && (data.telephone === undefined || data.telephone === '')) {
      toast.error('Email or phone must be required.');
      return false;
    }
    return true;
  }

  const handleAccordionSelect = (newActiveKey) => {
    setActiveKey(newActiveKey);
  }

  const fieldRow = (label, children) => (
    <div className='tw-grid tw-grid-cols-12 tw-gap-3 tw-mb-2 tw-items-center'>
      <div className='tw-col-span-12 sm:tw-col-span-3 tw-text-sm tw-font-medium tw-text-slate-700 lg:tw-text-right'>
        {label}
      </div>
      <div className='tw-col-span-12 sm:tw-col-span-9 md:tw-col-span-7'>
        {children}
      </div>
    </div>
  );

  const fieldError = (error) => (
    <span className='tw-text-red-600'>
      {error && error.message}
    </span>
  );

  return (
    <Card padding="none" bordered={!embedded} shadow={!embedded} className='tw-text-sm'>
      <form onSubmit={handleSubmit(onSubmit)}>
        {!embedded && (
          <div className='tw-px-4 tw-py-3 tw-flex tw-justify-between tw-items-center tw-border-b tw-border-solid tw-border-slate-200'>
            <h5 className='tw-mb-0 tw-font-semibold'>{title}</h5>
            <IconButton
              onClick={() => navigate(-1)}
              variant='secondary'
              size='sm'
              icon={<FaArrowLeft size={12} />}
            />
          </div>
        )}

        <div className={embedded ? 'tw-p-0' : 'tw-p-5'}>
          <Accordion
            className='tw-border tw-border-solid tw-border-slate-200 tw-rounded-sm tw-overflow-hidden tw-text-sm tw-mt-3'
            activeKeys={activeKey}
            onChange={handleAccordionSelect}
          >
            <Accordion.Item eventKey='0' header='1. Information'>
              <div className='tw-m-2 tw-p-3'>
                {fieldRow('Company name', (
                  <>
                    <input
                      type='text'
                      name='name'
                      placeholder='Company name'
                      className={INPUT_CLASSES}
                      {...register('name', {
                        required: 'Company name must be required.'
                      })}
                    />
                    {fieldError(errors.name)}
                  </>
                ))}

                {fieldRow('Country', (
                  <>
                    <Controller
                      control={control}
                      name='country'
                      render={() => (
                        <Select
                          closeMenuOnSelect={true}
                          options={countryOptions}
                          defaultValue={defaultcountryOption}
                          placeholder='Select...'
                          classNamePrefix='react-select'
                          onChange={selectedOption =>  {
                            setValue('country', selectedOption.value)
                            setCountryCode(selectedOption.value.toLowerCase())
                          }}
                        />
                      )}
                      rules={{
                        required: {
                          value: true,
                          message: 'Country is required'
                        }
                      }}
                    />
                    {fieldError(errors.country)}
                  </>
                ))}

                <div className='tw-mt-4'>
                  {fieldRow('Work Email', (
                    <>
                      <input
                        type='email'
                        name='email'
                        className={INPUT_CLASSES}
                        placeholder='example@pebsteel.com.vn'
                        {...register('email', {
                          //required: 'Email is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                            message: 'Invalid email address'
                          }
                        })}
                      />
                      {fieldError(errors.email)}
                    </>
                  ))}
                </div>

                {fieldRow('Work phone', (
                  <>
                    <Controller
                      control={control}
                      name='telephone'
                      render={({field: {ref, ...field}}) => (
                        <PhoneInput
                          {...field}
                          inputProps={{ref}}
                          country={customer ? customer.country?.toLowerCase() : countryCode }
                          autoFormat={true}
                          countryCodeEditable={customer ? true : false}
                          enableSearch={true}
                          disableSearchIcon={true}
                          inputStyle={{width: '100%'}}
                          searchStyle={{width: '95%'}}
                        />
                      )}
                      rules={{ validate: validatePhoneNumber }}
                    />
                    {fieldError(errors.telephone)}
                  </>
                ))}

                {fieldRow('Fax', (
                  <>
                    <Controller
                      control={control}
                      name='fax'
                      render={({field: {ref, ...field}}) => (
                        <PhoneInput
                          {...field}
                          inputProps={{ref}}
                          country={customer ? customer.country?.toLowerCase() : countryCode }
                          autoFormat={true}
                          countryCodeEditable={customer ? true : false}
                          enableSearch={true}
                          disableSearchIcon={true}
                          inputStyle={{width: '100%'}}
                          searchStyle={{width: '95%'}}
                        />
                      )}
                      rules={{ validate: validatePhoneNumber}}
                    />
                    {fieldError(errors.fax)}
                  </>
                ))}

                {fieldRow('EXT internal', (
                  <input
                    type='text'
                    name='ext_phone'
                    placeholder='123'
                    className={INPUT_CLASSES}
                    {...register('ext_phone')}
                  />
                ))}

                <div className='tw-mt-4'>
                  {fieldRow('Tax code', (
                    <input
                      type='text'
                      name='tax_code'
                      placeholder='12345678'
                      className={INPUT_CLASSES}
                      {...register('tax_code')}
                    />
                  ))}
                </div>

                {fieldRow('Website', (
                  <input
                    type='text'
                    name='website'
                    placeholder='https:\\example.com'
                    className={INPUT_CLASSES}
                    {...register('website')}
                  />
                ))}

                {!customer && (
                  <div className='tw-mt-4'>
                    {fieldRow('Address', (
                      <GoogleMapAutoCompletePlaceDebounce setAddress={setAddress} />
                    ))}
                  </div>
                )}
              </div>
            </Accordion.Item>

            <Accordion.Item eventKey='1' header='2. Legal status'>
              <div className='tw-m-2 tw-p-3'>
                {fieldRow('Legal status', (
                  <div className='tw-flex tw-flex-wrap tw-items-center tw-gap-3'>
                    {CRM_LEGAL_STATUS.map((status) => (
                      <label key={status} className='tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-cursor-pointer tw-mb-0'>
                        <input
                          type='radio'
                          name='legal_status'
                          value={status}
                          defaultChecked={status === 'Private'}
                          className='tw-cursor-pointer'
                          {...register('legal_status')}
                        />
                        {status}
                      </label>
                    ))}
                  </div>
                ))}

                {fieldRow('Type of customer', (
                  <>
                    <Controller
                      control={control}
                      name='type_of_company'
                      render={() => (
                        <Select
                          closeMenuOnSelect={true}
                          options={typeOfCustomerOptions}
                          defaultValue={defaultTypeOfCustomerOption}
                          placeholder='Select...'
                          classNamePrefix='react-select'
                          onChange={selectedOption => setValue('type_of_company', selectedOption.value)}
                        />
                      )}
                      rules={{
                        required: {
                          value: true,
                          message: 'Type of customer is required'
                        }
                      }}
                    />
                    {fieldError(errors.type_of_company)}
                  </>
                ))}

                {fieldRow('Number of employees', (
                  <Controller
                    control={control}
                    name='employess'
                    render={() => (
                      <Select
                        closeMenuOnSelect={true}
                        options={numberOfEmployeesOptions}
                        defaultValue={defaultNumberOfEmployeesOption}
                        placeholder='Select...'
                        classNamePrefix='react-select'
                        onChange={selectedOption => setValue('employess', selectedOption.value)}
                      />
                    )}
                  />
                ))}

                {fieldRow('Industry', (
                  <>
                    <Controller
                      control={control}
                      name='industry'
                      render={() => (
                        <Select
                          closeMenuOnSelect={true}
                          options={industryOptions}
                          defaultValue={defaultIndustryOption}
                          placeholder='Select...'
                          classNamePrefix='react-select'
                          onChange={selectedOption => setValue('industry', selectedOption.value)}
                        />
                      )}
                      rules={{
                        required: {
                          value: true,
                          message: 'Industry is required'
                        }
                      }}
                    />
                    {fieldError(errors.industry)}
                  </>
                ))}
              </div>
            </Accordion.Item>

            <Accordion.Item eventKey='2' header='3. Responsible'>
              <div className='tw-m-2 tw-p-3'>
                {fieldRow('Main contact', (
                  <EmployeeSelectField
                    name='main_contact'
                    control={control}
                    loadOptions={employeeOptions}
                    required
                    defaultValue={defaultMainContactOption}
                    placeholder='Select or input name...'
                  />
                ))}

                {fieldRow('Secondary contact', (
                  <EmployeeSelectField
                    name='secondary_contact'
                    control={control}
                    loadOptions={employeeOptions}
                    isMulti
                    defaultValue={defaultSecondaryContactOption}
                    placeholder='Select...'
                  />
                ))}
              </div>
            </Accordion.Item>

            <Accordion.Item eventKey='3' header='4. Upload logo'>
              <div className='tw-m-2 tw-p-3'>
                {fieldRow('', (
                  <CustomerUploadLogo {...{logo, setLogo, customer}}/>
                ))}
              </div>
            </Accordion.Item>
          </Accordion>
        </div>

        <div className={embedded
          ? 'tw-border-t tw-border-solid tw-border-slate-200 tw-text-center tw-p-3 tw-mt-3'
          : 'tw-bg-slate-50 tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-10 tw-text-center tw-p-3 tw-ml-12'}>
          <Button size='sm' type='submit' variant='primary' className='tw-mr-2'><FaSave /> Save</Button>
          <Button size='sm' variant='danger' onClick={handleCancle}><FaBan /> Cancel</Button>
        </div>
      </form>
    </Card>
  )
};

CustomerForm.propTypes = {
  title: PropTypes.string.isRequired,
  customer: PropTypes.object,
  embedded: PropTypes.bool,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func
}

export default CustomerForm;
